import React, { useEffect, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import LoginModal from "../LoginModal";

const EmployeeManager = () => {
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [form, setForm] = useState({
        name: "",
        email: "",
        employeeCode: "",
        department: "DESIGN",
        position: "INTERN",
        role: "USER",
    });
    const [selected, setSelected] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [enums, setEnums] = useState({ departments: [], positions: [], roles: [] });
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({ department: "", position: "" });
    const [isAdmin, setIsAdmin] = useState(false);
    const [checking, setChecking] = useState(true);
    const [loading, setLoading] = useState(false);
    const history = useHistory();

    const getLabel = (list, name) => {
        const found = list.find(item => item.name === name);
        return found ? found.label : name;
    };

    const verifyAdmin = async () => {
        try {
            const token = localStorage.getItem("auth_token");
            const res = await axios.post(`${process.env.REACT_APP_API_REAL_URL}/auth/verify_admin`, { token });
            setIsAdmin(res.data.valid);
            if (!res.data.valid) {
                alert("관리자 이외엔 접속할 수 없습니다");
                history.push("/");
            }
        } catch {
            setIsAdmin(false);
        } finally {
            setChecking(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/employees/list`);
            setEmployees(res.data);
            setFilteredEmployees(res.data);
        } catch {
            setError("직원 목록 불러오기에 실패했습니다.");
        }
    };

    const fetchEnums = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/employees/enums`);
            setEnums(res.data);
        } catch {
            setError("Enum 정보 불러오기에 실패했습니다.");
        }
    };

    useEffect(() => { verifyAdmin(); }, []);
    useEffect(() => { if (isAdmin) { fetchEmployees(); fetchEnums(); } }, [isAdmin]);

    useEffect(() => {
        let result = [...employees];
        if (search.trim()) {
            result = result.filter(e => e.name.includes(search) || e.email.includes(search));
        }
        if (filters.department) {
            result = result.filter(e => e.department === filters.department);
        }
        if (filters.position) {
            result = result.filter(e => e.position === filters.position);
        }
        setFilteredEmployees(result);
    }, [search, filters, employees]);

    const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

    const handleCreate = async () => {
        if (!form.name || !form.email) return alert("이름, 이메일은 필수입니다.");
        try {
            setLoading(true);
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/employees/create`, form);
            await fetchEmployees();
            setShowModal(false);
        } catch (e) {
            alert("추가 실패: " + (e.response?.data?.message || e.message));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (id) => {
        try {
            setLoading(true);
            await axios.put(`${process.env.REACT_APP_API_BASE_URL}/employees/update/${id}`, form);
            await fetchEmployees();
            setShowModal(false);
        } catch (e) {
            alert("수정 실패: " + (e.response?.data?.message || e.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("정말 삭제하시겠습니까?")) {
            try {
                setLoading(true);
                await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/employees/delete/${id}`);
                await fetchEmployees();
            } catch (e) {
                alert("삭제 실패: " + (e.response?.data?.message || e.message));
            } finally {
                setLoading(false);
            }
        }
    };

    const handlePasswordReset = async (emp) => {
        if (window.confirm(`정말 ${emp.name} (${emp.email}) 님의 비밀번호를 재설정하시겠습니까?`)) {
            try {
                setLoading(true);
                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/employees/reset_password/${emp.employeeCode}`);
                alert("비밀번호가 초기화되었습니다.");
                await fetchEmployees();
            } catch (e) {
                alert("비밀번호 초기화 실패: " + (e.response?.data?.message || e.message));
            } finally {
                setLoading(false);
            }
        }
    };

    const openCreateModal = () => {
        setForm({ name: "", email: "", employeeCode: "", department: "", position: "", role: "" });
        setSelected(null);
        setShowModal(true);
    };

    const openEditModal = (emp) => {
        setSelected(emp);
        setForm({
            name: emp.name,
            email: emp.email,
            employeeCode: emp.employeeCode,
            department: emp.department,
            position: emp.position,
            role: emp.role,
        });
        setShowModal(true);
    };

    if (checking) return <div className="flex justify-center items-center h-screen text-gray-500"> 관리자 권한 확인 중...</div>;
    if (!isAdmin) return <LoginModal onLoginSuccess={verifyAdmin} />;

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">직원 관리</h2>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow" onClick={openCreateModal}>+ 직원 추가</button>
            </div>

            <div className="flex gap-4 mb-4">
                <input
                    className="border px-3 py-2 rounded w-full"
                    placeholder="이름 또는 이메일 검색"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select
                    className="border px-3 py-2 rounded"
                    value={filters.department}
                    onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                >
                    <option value="">전체 부서</option>
                    {enums.departments.map((dep) => (
                        <option key={dep.name} value={dep.name}>{dep.label}</option>
                    ))}
                </select>
                <select
                    className="border px-3 py-2 rounded"
                    value={filters.position}
                    onChange={(e) => setFilters({ ...filters, position: e.target.value })}
                >
                    <option value="">전체 직급</option>
                    {enums.positions.map((pos) => (
                        <option key={pos.name} value={pos.name}>{pos.label}</option>
                    ))}
                </select>
            </div>

            <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-30 border-b">
                <tr>
                    <th className="px-3 py-2 text-left">이름</th>
                    <th className="px-3 py-2 text-left">이메일</th>
                    <th className="px-3 py-2 text-left">사번</th>
                    <th className="px-3 py-2 text-left">부서</th>
                    <th className="px-3 py-2 text-left">직급</th>
                    <th className="px-3 py-2 text-left">권한</th>
                    <th className="px-3 py-2 text-left">관리</th>
                </tr>
                </thead>
                <tbody>
                {filteredEmployees.map(emp => (
                    <tr key={emp.id} className="border-b hover:bg-gray-50 transition">
                        <td className="px-3 py-2">{emp.name}</td>
                        <td className="px-3 py-2">{emp.email}</td>
                        <td className="px-3 py-2">{emp.employeeCode}</td>
                        <td className="px-3 py-2">{getLabel(enums.departments, emp.department)}</td>
                        <td className="px-3 py-2">{getLabel(enums.positions, emp.position)}</td>
                        <td className="px-3 py-2">{getLabel(enums.roles, emp.role)}</td>
                        <td className="px-3 py-2 space-x-2">
                            <button onClick={() => openEditModal(emp)} className="text-blue-600 hover:underline">수정</button>
                            <button onClick={() => handleDelete(emp.id)} className="text-red-600 hover:underline">삭제</button>
                            <button onClick={() => handlePasswordReset(emp)} className="text-green-600 hover:underline">비밀번호 재설정</button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            {showModal && (
                <div className="slow-animation fixed inset-0 z-10 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white rounded-xl p-6 w-[420px] shadow-xl">
                        <h3 className="text-xl font-bold mb-4">{selected ? "직원 정보 수정" : "직원 추가"}</h3>
                        <div className="space-y-3">
                            <input name="name" value={form.name} onChange={handleChange} placeholder="이름" className="w-full border px-3 py-2 rounded" />
                            <input name="email" value={form.email} onChange={handleChange} placeholder="이메일" className="w-full border px-3 py-2 rounded" />
                            <select name="department" value={form.department} onChange={handleChange} className="w-full border px-3 py-2 rounded">
                                {enums.departments.map(dep => <option key={dep.name} value={dep.name}>{dep.label}</option>)}
                            </select>
                            <select name="position" value={form.position} onChange={handleChange} className="w-full border px-3 py-2 rounded">
                                {enums.positions.map(pos => <option key={pos.name} value={pos.name}>{pos.label}</option>)}
                            </select>
                            <select name="role" value={form.role} onChange={handleChange} className="w-full border px-3 py-2 rounded">
                                {enums.roles.map(role => <option key={role.name} value={role.name}>{role.label}</option>)}
                            </select>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button className="bg-gray-300 px-4 py-2 rounded" onClick={() => setShowModal(false)}>취소</button>
                            <button className="bg-blue-600 text-white px-4 py-2 rounded" disabled={loading} onClick={() => selected ? handleUpdate(selected.id) : handleCreate()}>
                                {loading ? "처리 중..." : (selected ? "저장" : "추가")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeManager;